import dayjs from 'dayjs'
import Decimal from 'decimal.js'
import { getManufactureSpecsForBin } from 'features/assetModeling'
import { getBinValueFunctions, useCdfBinReading } from 'features/dataLayers'
import { getInspectionDate } from 'features/fde/inspectionDateHelpers'
import { useGeckoLocale } from 'features/locale'
import { useMemo } from 'react'
import {
  GeckoComponentRect,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import type { RemainingLifeCalculationInspectorProps } from './RemainingLifeCalculationInspector'

export function useRemainingLifeCalculationData({
  assetModel,
  componentId,
  inspection,
  comparisonInspection,
  thicknessCdf,
  shortTermRateCdf,
  hoveredCoords,
  forecastComponentConfig,
  tminMethod,
}: RemainingLifeCalculationInspectorProps) {
  const {
    formatMeasurement,
    formatMeasurementDimension,
    getPreferredMeasurementUnit,
  } = useGeckoLocale()
  const binWidthFt = thicknessCdf?.data.binning.bin_size.width.convertTo(
    GeckoMeasurementUnit.Feet
  )
  const binHeightFt = thicknessCdf?.data.binning.bin_size.height.convertTo(
    GeckoMeasurementUnit.Feet
  )
  const preferredLinearUnit = getPreferredMeasurementUnit(
    GeckoMeasurementUnit.Inches
  )
  const binWidthDisplay =
    thicknessCdf?.data.binning.bin_size.width.convertTo(preferredLinearUnit)
  const binHeightDisplay =
    thicknessCdf?.data.binning.bin_size.height.convertTo(preferredLinearUnit)
  const inspectionDate =
    getInspectionDate({
      inspectionSlug: inspection?.id,
      inspection,
    }) ?? undefined
  const comparisonInspectionDate =
    getInspectionDate({
      inspectionSlug: comparisonInspection?.id,
      inspection: comparisonInspection,
    }) ?? undefined

  const coordsToUse = useMemo(
    () =>
      hoveredCoords?.convertTo(
        GeckoMeasurementUnit.Feet,
        GeckoMeasurementUnit.Feet
      ),
    [hoveredCoords]
  )

  const binRectForCoords = useMemo(() => {
    if (!coordsToUse || !binWidthFt || !binHeightFt) return null

    const halfBinWidth = binWidthFt.scale(0.5)
    const halfBinHeight = binHeightFt.scale(0.5)

    return GeckoComponentRect.CreateFromXYs(
      coordsToUse.componentID,
      coordsToUse.x.minus(halfBinWidth).num,
      coordsToUse.y.minus(halfBinHeight).num,
      coordsToUse.x.plus(halfBinWidth).num,
      coordsToUse.y.plus(halfBinHeight).num,
      coordsToUse.x.unit,
      coordsToUse.y.unit
    )
  }, [binHeightFt, binWidthFt, coordsToUse])

  const { value: thicknessAtCoords } = useCdfBinReading(
    thicknessCdf,
    coordsToUse
  )
  const { value: shortTermRateAtCoords } = useCdfBinReading(
    shortTermRateCdf,
    coordsToUse
  )

  const functions = useMemo(
    () =>
      getBinValueFunctions({
        assetModel,
        componentId,
        binHeightFt,
        binWidthFt,
        forecastComponentConfig,
        inspectionDate,
        tminMethod,
      }),
    [
      assetModel,
      binHeightFt,
      binWidthFt,
      componentId,
      forecastComponentConfig,
      inspectionDate,
      tminMethod,
    ]
  )

  const calculated = useMemo(() => {
    if (
      !assetModel ||
      !componentId ||
      !inspectionDate ||
      !binRectForCoords ||
      !coordsToUse ||
      !thicknessAtCoords
    ) {
      return undefined
    }

    const thicknessInches = thicknessAtCoords.convertTo(
      GeckoMeasurementUnit.Inches
    )
    const { nominalThickness, installationDate } = getManufactureSpecsForBin({
      binBoundsToGetSpecsFor: binRectForCoords,
      assetModel,
      latestDateToUse: inspectionDate.toDate(),
    })
    const nominal = nominalThickness
      ? weightThicknessValue(nominalThickness)
      : undefined

    const loss = functions.lossForCoordsFn?.(binRectForCoords, thicknessInches)
    const tmin = functions.tminForCoordsFn?.(binRectForCoords, thicknessInches)
    const shortTermRate = shortTermRateAtCoords?.convertTo(
      GeckoMeasurementUnit.MillimetersPerYear
    )

    const shortTermInterval =
      comparisonInspectionDate && inspectionDate
        ? GeckoMeasurement.CreateFromValue(
            new Decimal(inspectionDate.diff(comparisonInspectionDate, 'd'))
              .div(365)
              .toNumber(),
            GeckoMeasurementUnit.Years
          )
        : undefined

    const longTermAge =
      installationDate && inspectionDate
        ? GeckoMeasurement.CreateFromValue(
            new Decimal(inspectionDate.diff(installationDate, 'd'))
              .div(365)
              .toNumber(),
            GeckoMeasurementUnit.Years
          )
        : undefined

    return {
      coordsToUse,
      thickness: thicknessInches,
      nominal,
      loss,
      tmin,
      corrosionRate: shortTermRate,
      age: comparisonInspectionDate ? shortTermInterval : longTermAge,
      installationDate,
    }
  }, [
    assetModel,
    binRectForCoords,
    componentId,
    comparisonInspectionDate,
    coordsToUse,
    functions,
    inspectionDate,
    shortTermRateAtCoords,
    thicknessAtCoords,
  ])

  const formatValue = (
    measurement: GeckoMeasurement | undefined,
    options: { digits?: number; variant?: 'geometric' } = {}
  ) =>
    formatMeasurement({
      measurement,
      minimumFractionDigits: options.digits ?? 2,
      maximumFractionDigits: options.digits ?? 2,
      variant: options.variant,
    }) ?? '-'

  const coordsLabel = coordsToUse
    ? `${formatCoordinate(coordsToUse.x, preferredLinearUnit)}, ${formatCoordinate(
        coordsToUse.y,
        preferredLinearUnit
      )}`
    : '-, -'
  const binSize =
    binWidthDisplay && binHeightDisplay
      ? (formatMeasurementDimension({
          width: binWidthDisplay,
          height: binHeightDisplay,
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        }) ?? '-')
      : '-'
  const zero = GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Inches)
  const toInchesNumber = (measurement: GeckoMeasurement | undefined) => {
    const inches = measurement?.convertTo(GeckoMeasurementUnit.Inches)
    return inches && Number.isFinite(inches.num) ? inches.num : undefined
  }

  return {
    coordsLabel,
    binSize,
    thickness: formatValue(calculated?.thickness, { digits: 3 }),
    nominal: formatValue(calculated?.nominal, { digits: 3 }),
    loss: formatValue(calculated?.loss, { digits: 3 }),
    zero: calculated ? formatValue(zero, { digits: 3 }) : '-',
    damageValues: {
      nominal: toInchesNumber(calculated?.nominal),
      thickness: toInchesNumber(calculated?.thickness),
      tmin: toInchesNumber(calculated?.tmin),
    },
    corrosionRate: formatValue(calculated?.corrosionRate, { digits: 4 }),
    age: formatValue(calculated?.age, { digits: 1 }),
    inServiceDate: calculated?.installationDate
      ? dayjs(calculated.installationDate).format('MMM D, YYYY')
      : '-',
    inspectionDate: inspectionDate ? inspectionDate.format('MMM D, YYYY') : '-',
    comparisonInspectionDate: comparisonInspectionDate
      ? comparisonInspectionDate.format('MMM D, YYYY')
      : '-',
    ageLabel: comparisonInspectionDate ? 'Interval' : 'Age',
    inspectionDateLabel: comparisonInspectionDate ? 'Target' : 'Inspection',
    inServiceDateLabel: comparisonInspectionDate ? 'Prior' : 'In-service',
    formulaHelp:
      'STCR = high-end thickness loss / years between inspections. Thickness loss is calculated from matched target and prior damage zones, using prior thickness minus target thickness.',
  }

  function formatCoordinate(
    measurement: GeckoMeasurement,
    unit: GeckoMeasurementUnit
  ): string {
    return (
      formatMeasurement({
        measurement: measurement.convertTo(unit),
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }) ?? '-'
    )
  }
}

function weightThicknessValue(
  thicknessMeasurement: GeckoMeasurement
): GeckoMeasurement {
  if (
    thicknessMeasurement.unit === GeckoMeasurementUnit.Millimeters ||
    thicknessMeasurement.unit === GeckoMeasurementUnit.Inches
  ) {
    return thicknessMeasurement
  }

  return thicknessMeasurement.convertTo(GeckoMeasurementUnit.Millimeters)
}
